import { useState } from 'react'

import { useProject } from '@/modules/projects/hooks'
import {
  useCaseAccountTypes,
  useCaseTypes,
  useFetchCaseConfig,
} from '@/modules/projects/hooks/caseConfig'
import { cn } from '@/utils'
import { CaseDetailValue } from './CaseDetailValue'
import { CaseTypeChangeConfirm } from './CaseTypeChangeConfirm'
import {
  ACCOUNTS_SCOPE_CELL_KEY,
  CASE_TYPE_CELL_KEY,
  type CaseDetailCell,
  caseDetailCells,
  planCaseTypeChange,
} from './caseDetailCells'
import { useCaseDetailSaves } from './useCaseDetailSaves'

interface CaseDetailsGridProps {
  className?: string
}

// The case setup answers, read live from the project. Each value is edited and saved on its own.
export const CaseDetailsGrid = ({ className }: CaseDetailsGridProps) => {
  const project = useProject()
  useFetchCaseConfig()
  const caseTypes = useCaseTypes()
  const accountTypes = useCaseAccountTypes()
  const { saveDetail, saveAccountsScope, saveCaseType } = useCaseDetailSaves()
  const [pendingCaseType, setPendingCaseType] = useState<string | null>(null)

  if (!project) return null

  const config = { caseTypes, accountTypes }

  // Planned on every render, so the list of deleted fields follows edits made while it shows.
  const pendingChange =
    pendingCaseType === null
      ? null
      : planCaseTypeChange(project, config, pendingCaseType)

  const changeCaseType = async (nextCaseType: string) => {
    const change = planCaseTypeChange(project, config, nextCaseType)
    if (change.droppedLabels.length > 0) {
      setPendingCaseType(nextCaseType)
      return
    }
    await saveCaseType(nextCaseType, config)
  }

  const saveCell = (cell: CaseDetailCell) => (value: string) => {
    if (cell.key === CASE_TYPE_CELL_KEY) return changeCaseType(value)
    if (cell.key === ACCOUNTS_SCOPE_CELL_KEY) return saveAccountsScope(value)
    return saveDetail(cell.key, value)
  }

  return (
    <div className={cn('space-y-4', className)}>
      {pendingChange && (
        <CaseTypeChangeConfirm
          change={pendingChange}
          onConfirm={() =>
            saveCaseType(pendingChange.caseType, config).then(() =>
              setPendingCaseType(null),
            )
          }
          onCancel={() => setPendingCaseType(null)}
        />
      )}
      <dl className="grid grid-cols-2 content-start gap-x-6 gap-y-4 @2xl:grid-cols-3">
        {caseDetailCells(project, config).map((cell) => (
          <div key={cell.key} className="min-w-0">
            <dt className="text-xs text-ink-subtle">{cell.label}</dt>
            <dd>
              <CaseDetailValue cell={cell} onSave={saveCell(cell)} />
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}
